import { useState, useEffect, useCallback } from "react";
import {
  X,
  Receipt,
  CreditCard,
  Banknote,
  Check,
  Building2,
  Printer,
  Loader2,
} from "lucide-react";
import medicalDB from "../services/database";
import Toast from "./Toast";
import ConfirmDialog from "./ConfirmDialog";

const VisitBillingPopup = ({
  isOpen,
  onClose,
  onConfirm,
  patient,
  visitData,
  visitTypeId,
  performedActions = [],
}) => {
  const [billingData, setBillingData] = useState(null);
  const [editableItems, setEditableItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(
    medicalDB.PAYMENT_METHODS?.CASH || "cash"
  );
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  // Payment method options
  const paymentMethods = [
    {
      value: medicalDB.PAYMENT_METHODS?.CASH || "cash",
      label: "Cash",
      icon: Banknote,
    },
    {
      value: medicalDB.PAYMENT_METHODS?.CARD || "card",
      label: "Card",
      icon: CreditCard,
    },
    {
      value: medicalDB.PAYMENT_METHODS?.CHECK || "check",
      label: "Check",
      icon: Check,
    },
    {
      value: medicalDB.PAYMENT_METHODS?.BANK_TRANSFER || "bank_transfer",
      label: "Bank Transfer",
      icon: Building2,
    },
  ];

  const calculateBilling = useCallback(async () => {
    try {
      setLoading(true);
      const billing = await medicalDB.calculateVisitBilling(
        visitData,
        visitTypeId,
        performedActions
      );
      setBillingData(billing);
      setEditableItems(billing.items);
      setTotalAmount(billing.totalAmount);
      setPaidAmount(billing.totalAmount); // Default to full payment
    } catch (error) {
      console.error("Error calculating billing:", error);
    } finally {
      setLoading(false);
    }
  }, [visitData, visitTypeId, performedActions]);

  // Calculate billing when popup opens
  useEffect(() => {
    if (isOpen && visitData) {
      calculateBilling();
    }
  }, [isOpen, visitData, calculateBilling]);

  // Update item price
  const updateItemPrice = (index, newPrice) => {
    const updatedItems = [...editableItems];
    updatedItems[index].price = parseFloat(newPrice) || 0;
    setEditableItems(updatedItems);

    // Recalculate total
    const newTotal = updatedItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
    setTotalAmount(newTotal);

    // Update paid amount to match new total if it was previously equal
    if (paidAmount === totalAmount) {
      setPaidAmount(newTotal);
    }
  };

  // Calculate change
  const change = Math.max(0, paidAmount - totalAmount);
  const balance = totalAmount - paidAmount;

  // Handle confirm button click - show confirmation dialog
  const handleConfirmClick = () => {
    setShowConfirm(true);
  };

  // Handle actual confirmation after user confirms
  const handleConfirm = async () => {
    setShowConfirm(false);

    try {
      setLoading(true);

      const transactionData = {
        items: editableItems,
        totalAmount,
        paidAmount,
        paymentMethod,
        notes,
      };

      await onConfirm(transactionData);

      setToast({
        type: "success",
        message: "Visit and billing information saved successfully!",
      });

      // Close after showing success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error confirming billing:", error);
      setToast({
        type: "error",
        message: `Failed to save: ${error.message}`,
      });
      setLoading(false);
    }
  };

  // Handle print receipt
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    const receiptHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Medical Receipt</title>
          <style>
            body {
              font-family: 'Courier New', monospace;
              margin: 0;
              padding: 20px;
              font-size: 12px;
              line-height: 1.4;
            }
            .receipt {
              max-width: 300px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
              margin-bottom: 15px;
            }
            .clinic-name {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .receipt-title {
              font-size: 14px;
              font-weight: bold;
              margin: 10px 0;
            }
            .info-line {
              display: flex;
              justify-content: space-between;
              margin: 3px 0;
            }
            .items {
              border-top: 1px dashed #000;
              border-bottom: 1px dashed #000;
              padding: 10px 0;
              margin: 10px 0;
            }
            .item {
              display: flex;
              justify-content: space-between;
              margin: 5px 0;
            }
            .total-section {
              margin-top: 10px;
            }
            .total-line {
              display: flex;
              justify-content: space-between;
              margin: 3px 0;
              font-weight: bold;
            }
            .footer {
              text-align: center;
              margin-top: 20px;
              border-top: 1px dashed #000;
              padding-top: 10px;
              font-size: 10px;
            }
            @media print {
              body { margin: 0; padding: 10px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="header">
              <div class="clinic-name">Medical Clinic</div>
              <div>Receipt</div>
            </div>
            
            <div class="receipt-title">VISIT RECEIPT</div>
            
            <div class="info-line">
              <span>Date:</span>
              <span>${currentDate}</span>
            </div>
            <div class="info-line">
              <span>Time:</span>
              <span>${currentTime}</span>
            </div>
            <div class="info-line">
              <span>Patient:</span>
              <span>${patient?.name || "N/A"}</span>
            </div>
            
            <div class="items">
              ${editableItems
                .map(
                  (item) => `
                <div class="item">
                  <span>${item.description}</span>
                  <span>₪${item.amount.toFixed(2)}</span>
                </div>
              `
                )
                .join("")}
            </div>
            
            <div class="total-section">
              <div class="info-line">
                <span>Subtotal:</span>
                <span>₪${totalAmount.toFixed(2)}</span>
              </div>
              <div class="total-line">
                <span>Total:</span>
                <span>₪${totalAmount.toFixed(2)}</span>
              </div>
              <div class="info-line">
                <span>Paid:</span>
                <span>₪${paidAmount.toFixed(2)}</span>
              </div>
              ${
                change > 0
                  ? `
                <div class="info-line">
                  <span>Change:</span>
                  <span>₪${change.toFixed(2)}</span>
                </div>
              `
                  : ""
              }
              ${
                balance > 0
                  ? `
                <div class="info-line">
                  <span>Balance Due:</span>
                  <span>₪${balance.toFixed(2)}</span>
                </div>
              `
                  : ""
              }
            </div>
            
            <div class="info-line">
              <span>Payment Method:</span>
              <span>${
                paymentMethods.find((pm) => pm.value === paymentMethod)
                  ?.label || paymentMethod
              }</span>
            </div>
            
            ${
              notes
                ? `
              <div style="margin-top: 10px;">
                <div>Notes:</div>
                <div style="font-size: 10px; margin-top: 5px;">${notes}</div>
              </div>
            `
                : ""
            }
            
            <div class="footer">
              <div>Thank you for your visit!</div>
              <div>Keep this receipt for your records</div>
            </div>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(receiptHtml);
    printWindow.document.close();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Receipt className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Visit Billing
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Calculating billing...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Receipt Header */}
              <div className="text-center border-b border-gray-200 pb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Medical Receipt
                </h3>
                <p className="text-sm text-gray-500">
                  {new Date().toLocaleDateString("en-IL")} •{" "}
                  {new Date().toLocaleTimeString("en-IL", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>

              {/* Line Items */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900 mb-3">
                  Services & Items
                </h4>
                {editableItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {item.name}
                        {item.dose && item.unit && (
                          <span className="text-sm text-gray-500 ml-2">
                            ({item.dose} {item.unit})
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 capitalize">
                        {item.type} {item.category && `• ${item.category}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">₪</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price}
                        onFocus={(e) => {
                          // Select the field if it's 0
                          if (parseFloat(e.target.value) === 0) {
                            e.target.select();
                          }
                        }}
                        onChange={(e) => updateItemPrice(index, e.target.value)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>Total Amount:</span>
                  <span>₪{totalAmount.toFixed(2)}</span>
                </div>

                {/* Payment Method */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Payment Method
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {paymentMethods.map((method) => {
                      const IconComponent = method.icon;
                      return (
                        <button
                          key={method.value}
                          onClick={() => setPaymentMethod(method.value)}
                          className={`flex items-center gap-2 p-3 border rounded-lg transition-colors ${
                            paymentMethod === method.value
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <IconComponent className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            {method.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Payment Amount */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Amount Paid
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">₪</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={paidAmount}
                      onFocus={(e) => {
                        // Select the field if it's 0
                        if (parseFloat(e.target.value) === 0) {
                          e.target.select();
                        }
                      }}
                      onChange={(e) =>
                        setPaidAmount(parseFloat(e.target.value) || 0)
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Change/Balance */}
                {change > 0 && (
                  <div className="flex justify-between items-center text-green-600 font-medium">
                    <span>Change:</span>
                    <span>₪{change.toFixed(2)}</span>
                  </div>
                )}

                {balance > 0 && (
                  <div className="flex justify-between items-center text-orange-600 font-medium">
                    <span>Outstanding Balance:</span>
                    <span>₪{balance.toFixed(2)}</span>
                  </div>
                )}

                {/* Notes */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Payment notes, special instructions, etc."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handlePrint}
            disabled={!editableItems.length || totalAmount <= 0}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            Print Receipt
          </button>
          <button
            onClick={handleConfirmClick}
            disabled={loading || totalAmount <= 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Receipt className="h-4 w-4" />
                Confirm & Save Visit
              </>
            )}
          </button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirm}
        title="Confirm Visit & Billing"
        message={`Save this visit with a total amount of ₪${totalAmount.toFixed(
          2
        )} (Paid: ₪${paidAmount.toFixed(2)}${
          balance > 0 ? `, Outstanding: ₪${balance.toFixed(2)}` : ""
        })?`}
        type="success"
        confirmText="Yes, Save Visit"
        cancelText="Cancel"
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
      />

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default VisitBillingPopup;
